/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AttributeUpdateDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, {
  useCallback,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomTabsV2 from "../../../components/customComponentsV2/common/tabs/CustomTabsV2";
import PropTypes from "prop-types";
import AttributesForm from "./AttributesForm";
import { HandleApiActions, labCar } from "../../../redux/actions";
import { useDispatch } from "react-redux";
import { Stack, Box, Typography } from "@mui/material";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";

const EMPTY_ATTRIBUTE_FORM_DATA = {
  added: [],
  edited: [],
  deleted: [],
};

function AttributeUpdateDialog(
  {
    openAttributeUpdateDialog,
    setOpenAttributeUpdateDialog,
    handleCloseAttributeUpdateDialog,
    onClickSuccess,
    selectedComponent,
    componentType,
    onShowUpdateDialog, // Callback to open update EE dialog from parent
    type = "EDIT",
    hideDefaultActions = false,
    isAttributeImpactFlow = false,
    onAttributeImpactComplete,
    onIntersectionNo,
    attributeImpactActionType = { reset: true },
    variantId,
    reason,
    setReason,
    reasonError,
  },
  ref,
) {
  const dispatch = useDispatch();

  const [attributeFormData, setAttributeFormData] = useState(
    EMPTY_ATTRIBUTE_FORM_DATA,
  );
  const [currentTab, setCurrentTab] = useState(1);

  const [showVersionImpactDialog, setShowVersionImpactDialog] = useState(false);
  const [showDependenciesDialog, setShowDependenciesDialog] = useState(false);
  const [showIntersectionsDialog, setShowIntersectionsDialog] = useState(false);
  const [disableActionButton, setDisableActionButton] = useState(false);
  const [localReason, setLocalReason] = useState("");

  const dialogReason = reason ?? localReason;
  const handleReasonChange = useCallback(
    (value) => {
      if (setReason) {
        setReason(value);
        return;
      }

      setLocalReason(value);
    },
    [setReason],
  );

  const hasAttributeChanges =
    (Array.isArray(attributeFormData?.added) &&
      attributeFormData.added.length > 0) ||
    (Array.isArray(attributeFormData?.edited) &&
      attributeFormData.edited.length > 0) ||
    (Array.isArray(attributeFormData?.deleted) &&
      attributeFormData.deleted.length > 0);
  const hasReason = String(dialogReason ?? "").trim().length >= 3;
  const isEditDisabled =
    disableActionButton || !hasAttributeChanges || !hasReason || !!reasonError;

  const resetAttributeDialogState = useCallback(() => {
    setAttributeFormData(EMPTY_ATTRIBUTE_FORM_DATA);
    setCurrentTab(1);
    setShowVersionImpactDialog(false);
    setShowDependenciesDialog(false);
    setShowIntersectionsDialog(false);
    setDisableActionButton(false);
    handleReasonChange("");
  }, [handleReasonChange]);

  const closeAttributeDialog = useCallback(() => {
    setOpenAttributeUpdateDialog(false);
    resetAttributeDialogState();
    handleCloseAttributeUpdateDialog?.();
  }, [
    handleCloseAttributeUpdateDialog,
    resetAttributeDialogState,
    setOpenAttributeUpdateDialog,
  ]);

  useEffect(() => {
    if (!openAttributeUpdateDialog) {
      resetAttributeDialogState();
    }
  }, [openAttributeUpdateDialog, resetAttributeDialogState]);

  const handleAttributeUpdate = useCallback((data = {}) => {
    const added = Array.isArray(data?.added) ? data.added : [];
    const edited = Array.isArray(data?.edited) ? data.edited : [];
    const deleted = Array.isArray(data?.deleted) ? data.deleted : [];

    setAttributeFormData((prev) => {
      const nextData = { added, edited, deleted };
      return JSON.stringify(prev) === JSON.stringify(nextData)
        ? prev
        : nextData;
    });
  }, []);

  const getAttributeUpdateDetails = () => {
    let apiDetails = null;
    let entityId = null;

    switch (componentType) {
      case "ee":
        apiDetails = labCar.updateEEAttribute;
        entityId = selectedComponent?.ee_id;
        break;
      case "swm":
        apiDetails = labCar.updateSWMAttribute;
        entityId = selectedComponent?.swm_id;
        break;
      case "vs":
        apiDetails = labCar.updateVSAttribute;
        entityId = selectedComponent?.vs_id;
        break;
      case "vc":
        apiDetails = labCar.updateVCAttribute;
        entityId = selectedComponent?.vc_id;
        break;
      default:
        break;
    }

    return { apiDetails, entityId };
  };

  const getAttributePayload = () => ({
    new_attributes: Array.isArray(attributeFormData?.added)
      ? attributeFormData.added
      : [],
    updated_attributes: Array.isArray(attributeFormData?.edited)
      ? attributeFormData.edited
      : [],
    deleted_attributes: Array.isArray(attributeFormData?.deleted)
      ? attributeFormData.deleted
      : [],
  });

  const getActionTypeEntityPayload = () => ({
    project_id: Number(selectedComponent?.project_id),
    variant_id: Number(variantId ?? selectedComponent?.variant_id),
    ee_id: Number(selectedComponent?.ee_id),
    ...(componentType === "swm" && {
      swm_id: Number(selectedComponent?.swm_id),
    }),
  });

  const handleAttributeImpactEdit = () => {
    setDisableActionButton(true);

    const { apiDetails, entityId } = getAttributeUpdateDetails();
    const attributePayload = getAttributePayload();

    const finishImpactEdit = () => {
      dispatch(
        HandleApiActions({
          ...labCar.update_action_type,
          params: {
            current_time: new Date().toISOString(),
            ...getActionTypeEntityPayload(),
            is_dependency_linking: false,
            ...attributeImpactActionType,
          },
          show_toast: false,
        }),
      )
        .then(() => {
          setOpenAttributeUpdateDialog(false);
          resetAttributeDialogState();
          onAttributeImpactComplete?.();
          setDisableActionButton(false);
        })
        .catch((error) => { console.warn(error);
          setDisableActionButton(false);
        });
    };

    if (apiDetails) {
      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            entity_id: Number(entityId),
            ...attributePayload,
          },
          show_toast: false,
        }),
      )
        .then(() => {
          finishImpactEdit();
        })
        .catch((error) => { console.warn(error);
          setDisableActionButton(false);
        });
    } else {
      finishImpactEdit();
    }
  };

  const handleAttributeUpdateAndShowVersionDialog = () => {
    if (isEditDisabled) {
      return;
    }

    if (isAttributeImpactFlow) {
      handleAttributeImpactEdit();
      return;
    }

    setShowVersionImpactDialog(true);
  };

  const handleVersionImpactNo = () => {
    setDisableActionButton(true);
    setShowVersionImpactDialog(false);

    let apiDetails = null;
    let entityId = null;
    switch (componentType) {
      case "ee":
        apiDetails = labCar.updateEEAttribute;
        entityId = selectedComponent?.ee_id;
        break;
      case "swm":
        apiDetails = labCar.updateSWMAttribute;
        entityId = selectedComponent?.swm_id;
        break;
      case "vs":
        apiDetails = labCar.updateVSAttribute;
        entityId = selectedComponent?.vs_id;
        break;
      case "vc":
        apiDetails = labCar.updateVCAttribute;
        entityId = selectedComponent?.vc_id;
        break;
      default:
        break;
    }

    const addedAttributes = Array.isArray(attributeFormData?.added)
      ? attributeFormData.added
      : [];
    const updatedAttributes = Array.isArray(attributeFormData?.edited)
      ? attributeFormData.edited
      : [];
    const deletedAttributes = Array.isArray(attributeFormData?.deleted)
      ? attributeFormData.deleted
      : [];

    if (apiDetails) {
      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            entity_id: Number(entityId),
            new_attributes: addedAttributes,
            updated_attributes: updatedAttributes,
            deleted_attributes: deletedAttributes,
          },
          show_toast: true,
        }),
      )
        .then(() => {
          setTimeout(() => {
            setShowDependenciesDialog(true);
          }, 200);
        })
        .catch((error) => { console.warn(error);
          setDisableActionButton(false);
        });
    } else {
      setTimeout(() => {
        setShowDependenciesDialog(true);
      }, 200);
    }
  };

  const handleVersionImpactYes = () => {
    setDisableActionButton(true);
    setShowVersionImpactDialog(false);

    let apiDetails = null;
    let entityId = null;
    switch (componentType) {
      case "ee":
        apiDetails = labCar.updateEEAttribute;
        entityId = selectedComponent?.ee_id;
        break;
      case "swm":
        apiDetails = labCar.updateSWMAttribute;
        entityId = selectedComponent?.swm_id;
        break;
      case "vs":
        apiDetails = labCar.updateVSAttribute;
        entityId = selectedComponent?.vs_id;
        break;
      case "vc":
        apiDetails = labCar.updateVCAttribute;
        entityId = selectedComponent?.vc_id;
        break;
      default:
        break;
    }

    const addedAttributes = Array.isArray(attributeFormData?.added)
      ? attributeFormData.added
      : [];
    const updatedAttributes = Array.isArray(attributeFormData?.edited)
      ? attributeFormData.edited
      : [];
    const deletedAttributes = Array.isArray(attributeFormData?.deleted)
      ? attributeFormData.deleted
      : [];

    const finishVersionImpactYes = () => {
      dispatch(
        HandleApiActions({
          ...labCar.update_action_type,
          params: {
            current_time: new Date().toISOString(),
            ...getActionTypeEntityPayload(),
            is_dependency_linking: false,
            version: true,
          },
          show_toast: false,
        }),
      )
        .then(() => {
          setOpenAttributeUpdateDialog(false);
          resetAttributeDialogState();
          setDisableActionButton(false);

          if (onShowUpdateDialog) {
            onShowUpdateDialog();
          }
        })
        .catch((error) => { console.warn(error);
          setDisableActionButton(false);
        });
    };

    if (apiDetails) {
      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            entity_id: Number(entityId),
            new_attributes: addedAttributes,
            updated_attributes: updatedAttributes,
            deleted_attributes: deletedAttributes,
          },
          show_toast: false,
        }),
      )
        .then(() => {
          finishVersionImpactYes();
        })
        .catch((error) => { console.warn(error);
          setDisableActionButton(false);
        });
    } else {
      finishVersionImpactYes();
    }
  };

  const handleDependenciesNo = () => {
    setShowDependenciesDialog(false);
    setTimeout(() => {
      setShowIntersectionsDialog(true);
    }, 200);
  };

  const handleDependenciesYes = () => {
    setDisableActionButton(true);
    setShowDependenciesDialog(false);

    dispatch(
      HandleApiActions({
        ...labCar.update_action_type,
        params: {
          current_time: new Date().toISOString(),
          ...getActionTypeEntityPayload(),
          is_dependency_linking: false,
          dependency: true,
        },
        show_toast: false,
      }),
    )
      .then(() => {
        setOpenAttributeUpdateDialog(false);
        resetAttributeDialogState();
        onClickSuccess(null);
      })
      .catch((error) => { console.warn(error);
        setDisableActionButton(false);
      });
  };

  const handleIntersectionsNo = async () => {
    setShowIntersectionsDialog(false);
    setDisableActionButton(true);
    await onIntersectionNo?.();
    setOpenAttributeUpdateDialog(false);
    resetAttributeDialogState();
    onClickSuccess(null);
    setDisableActionButton(false);
  };

  const handleIntersectionsYes = () => {
    setDisableActionButton(true);
    setShowIntersectionsDialog(false);

    dispatch(
      HandleApiActions({
        ...labCar.update_action_type,
        params: {
          current_time: new Date().toISOString(),
          ...getActionTypeEntityPayload(),
          is_dependency_linking: false,
          intersection: true,
        },
        show_toast: false,
      }),
    )
      .then(() => {
        setOpenAttributeUpdateDialog(false);
        resetAttributeDialogState();
        onClickSuccess(null);
      })
      .catch((error) => { console.warn(error);
        setDisableActionButton(false);
      });
  };

  const handleContinueAfterVersionUpdate = () => {
    setDisableActionButton(true);

    dispatch(
      HandleApiActions({
        ...labCar.update_action_type,
        params: {
          current_time: new Date().toISOString(),
          ...getActionTypeEntityPayload(),
          is_dependency_linking: false,
          version: false,
        },
        show_toast: false,
      }),
    )
      .then(() => {
        setTimeout(() => {
          setShowDependenciesDialog(true);
        }, 200);
      })
      .catch((error) => { console.warn(error);
        setDisableActionButton(false);
      });
  };

  useImperativeHandle(
    ref,
    () => ({
      handleContinueAfterVersionUpdate,
    }),
    [selectedComponent, dispatch],
  );

  return (
    <>
      <CustomDialogBoxV2
        open={openAttributeUpdateDialog}
        onClose={closeAttributeDialog}
        title={type === "COPY" ? "Copy Attributes" : "Edit Attribute"}
        rejectButtonName={"Cancel"}
        successButtonName={"Edit"}
        actionButtonStyle={"continuous"}
        onClickReject={closeAttributeDialog}
        onClickSuccess={handleAttributeUpdateAndShowVersionDialog}
        onDisabled={isEditDisabled}
        hideDefaultActions={hideDefaultActions || type === "COPY"}
      >
        <CustomTabsV2
          defaultTab={1}
          value={currentTab}
          onTabChange={(newValue) => {
            if (newValue === 0) {
              return;
            }
            setCurrentTab(newValue);
          }}
          tabs={[
            {
              name: "Details",
              content: (
                <Stack
                  spacing={2}
                  padding={2}
                  mb={2}
                  sx={{ width: "450px" }}
                ></Stack>
              ),
            },
            {
              name: "Attribute",
              content: (
                <Stack
                  spacing={2}
                  mb={2}
                  justifyContent={"center"}
                  sx={{ width: "450px" }}
                >
                  <AttributesForm
                    type={type}
                    attributeFromApi={selectedComponent?.attributes || []}
                    getFormData={handleAttributeUpdate}
                    onCopyAttribute={() => {
                      setOpenAttributeUpdateDialog(false);
                      resetAttributeDialogState();
                    }}
                    reason={dialogReason}
                    setReason={handleReasonChange}
                    reasonError={reasonError}
                    showReason={true}
                  />
                </Stack>
              ),
            },
          ]}
        />
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={showVersionImpactDialog}
        onClose={() => {
          setShowVersionImpactDialog(false);
        }}
        title="Action"
        dialogActions={
          <Box
            sx={{
              display: "flex",
              width: "350px",
              justifyContent: "space-between",
              height: "50px",
            }}
          >
            <CustomButtonV2
              size="small"
              type="cancel"
              btnName="No"
              handleOnClick={handleVersionImpactNo}
            />
            <CustomButtonV2
              size="small"
              type="submit"
              btnName="Yes"
              handleOnClick={handleVersionImpactYes}
            />
          </Box>
        }
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body1">
            Will it impact the existing version?
          </Typography>
        </Box>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={showDependenciesDialog}
        onClose={() => {
          setShowDependenciesDialog(false);
        }}
        title="Action"
        dialogActions={
          <Box
            sx={{
              display: "flex",
              width: "350px",
              justifyContent: "space-between",
              height: "50px",
            }}
          >
            <CustomButtonV2
              size="small"
              type="cancel"
              btnName="No"
              handleOnClick={handleDependenciesNo}
            />
            <CustomButtonV2
              size="small"
              type="submit"
              btnName="Yes"
              handleOnClick={handleDependenciesYes}
            />
          </Box>
        }
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body1">
            Will it impact the existing dependencies?
          </Typography>
        </Box>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={showIntersectionsDialog}
        onClose={() => {
          setShowIntersectionsDialog(false);
        }}
        title="Action"
        dialogActions={
          <Box
            sx={{
              display: "flex",
              width: "350px",
              justifyContent: "space-between",
              height: "50px",
            }}
          >
            <CustomButtonV2
              size="small"
              type="cancel"
              btnName="No"
              handleOnClick={handleIntersectionsNo}
            />
            <CustomButtonV2
              size="small"
              type="submit"
              btnName="Yes"
              handleOnClick={handleIntersectionsYes}
            />
          </Box>
        }
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body1">
            Will it impact the existing intersections?
          </Typography>
        </Box>
      </CustomDialogBoxV2>
    </>
  );
}

AttributeUpdateDialog.propTypes = {
  openAttributeUpdateDialog: PropTypes.any,
  setOpenAttributeUpdateDialog: PropTypes.any,
  handleCloseAttributeUpdateDialog: PropTypes.any,
  onClickSuccess: PropTypes.any,
  selectedComponent: PropTypes.any,
  componentType: PropTypes.any,
  type: PropTypes.any,
  hideDefaultActions: PropTypes.any,
  reason: PropTypes.any,
  setReason: PropTypes.any,
  reasonError: PropTypes.any,
  isAttributeImpactFlow: PropTypes.any,
  onAttributeImpactComplete: PropTypes.any,
  onShowUpdateDialog: PropTypes.any,
  onIntersectionNo: PropTypes.any,
  attributeImpactActionType: PropTypes.any,
  variantId: PropTypes.any,
};

export default forwardRef(AttributeUpdateDialog);
